import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from './ui/badge';
import { getOptimizedImageUrl } from '../lib/imageOptimization';

export const ProjectModal = ({ project, onClose }) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const timerRef = useRef(null);

  const images = project ? (project.images || [project.image]) : [];
  const totalSlides = images.length;

  const goToSlide = useCallback((idx) => {
    setSlideIndex(idx);
  }, []);

  const nextSlide = useCallback(() => {
    setSlideIndex(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setSlideIndex(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  useEffect(() => {
    if (totalSlides > 1) {
      timerRef.current = setInterval(nextSlide, 2000);
      return () => clearInterval(timerRef.current);
    }
  }, [nextSlide, totalSlides]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!project) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300 md:flex md:items-center md:justify-center md:px-4 md:py-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative bg-white w-full h-full md:max-w-5xl md:mx-auto md:my-0 md:rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col md:max-h-[95vh] md:overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gray-900 flex-shrink-0 md:rounded-t-2xl overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-20 w-9 h-9 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-900 transition-colors duration-200 shadow-lg"
          >
            <X className="w-5 h-5" />
          </button>

          {totalSlides > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white/80 hover:text-white transition-all duration-200"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white/80 hover:text-white transition-all duration-200"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div className="aspect-[16/9] w-full bg-gray-900 flex items-center justify-center">
            <img
              src={getOptimizedImageUrl(images[slideIndex], {
                quality: 'auto',
                format: 'auto',
                crop: 'fit'
              })}
              alt={`${project.title} - ${slideIndex + 1}`}
              loading="lazy"
              className="w-full h-full object-contain"
              decoding="async"
            />
          </div>

          {totalSlides > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    idx === slideIndex
                      ? 'bg-white w-5'
                      : 'bg-white/50 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto md:overflow-visible p-5 sm:p-8 md:p-10">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge className="bg-brand-600 hover:bg-brand-700 text-white border-0 text-xs sm:text-sm">
              {project.category}
            </Badge>
            {/* <span className="text-gray-500 text-xs sm:text-sm">{project.year}</span> */}
            <Badge className={`${project.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'} border-0 text-xs sm:text-sm`}>
              {project.status === 'completed' ? 'Completed' : 'Ongoing'}
            </Badge>
          </div>

          <h2 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-2 sm:mb-3">
            {project.title}
          </h2>

          <div className="flex items-center text-gray-600 mb-3 sm:mb-4">
            <MapPin className="w-4 h-4 mr-1.5 text-brand-600 flex-shrink-0" />
            <span className="text-sm sm:text-base">{project.location}</span>
          </div>

          <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">
            {project.description}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3">Project Details</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span className="font-medium">Total Area:</span>
                  <span>{project.area}</span>
                </div>
                {/* <div className="flex justify-between">
                  <span className="font-medium">Completion:</span>
                  <span>{project.year}</span>
                </div> */}
                <div className="flex justify-between">
                  <span className="font-medium">Category:</span>
                  <span>{project.category}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3">Key Features</h3>
              <div className="grid grid-cols-1 gap-1.5">
                {project.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center text-sm text-gray-600"
                  >
                    <div className="w-1.5 h-1.5 bg-brand-600 rounded-full mr-2 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};